import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, CheckCheck } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";

export const NotificationBell = ({ align = "right" }) => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    axios.get(`${API}/my/notifications`, { headers: authHeaders() })
      .then((r) => setData(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => { clearInterval(t); document.removeEventListener("mousedown", onClick); };
  }, [load]);

  const readAll = async () => {
    await axios.post(`${API}/my/notifications/read`, {}, { headers: authHeaders() }).catch(() => {});
    load();
  };

  const openItem = async (item) => {
    if (!item.read) {
      axios.post(`${API}/my/notifications/read`, { ids: [item.id] }, { headers: authHeaders() }).then(load).catch(() => {});
    }
    setOpen(false);
    // job alerts (and anything with a link) deep-link straight to the relevant page
    if (item.link) navigate(item.link);
  };

  const timeAgo = (iso) => {
    const s = Math.floor((Date.now() - new Date(iso)) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notifications-bell"
        onClick={() => { setOpen((o) => !o); if (!open) load(); }}
        className="relative rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] p-2.5 text-[var(--pv-text-3)] transition-colors hover:text-[var(--pv-text)]"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {data.unread > 0 && (
          <span data-testid="notifications-badge" className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">
            {data.unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notifications-dropdown"
          className={`absolute top-12 z-[100] w-80 max-w-[calc(100vw-1.5rem)] rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] shadow-2xl max-sm:fixed max-sm:left-3 max-sm:right-3 max-sm:top-16 max-sm:w-auto ${
            align === "left" ? "left-0" : "right-0"
          }`}
        >
          <div className="flex items-center justify-between border-b border-[var(--pv-border)] px-4 py-3">
            <p className="text-xs font-bold uppercase tracking-wider text-[var(--pv-text-3)]">Notifications</p>
            {data.unread > 0 && (
              <button data-testid="notifications-read-all" onClick={readAll} className="flex items-center gap-1 text-[11px] font-semibold text-cyan-400 hover:text-cyan-300">
                <CheckCheck className="h-3 w-3" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {data.items.length === 0 ? (
              <p data-testid="notifications-empty" className="px-4 py-8 text-center text-xs text-[var(--pv-text-4)]">No notifications yet.</p>
            ) : (
              data.items.map((n) => (
                <button key={n.id} data-testid={`notification-${n.id}`} onClick={() => openItem(n)}
                  className={`block w-full border-b border-[var(--pv-border)] px-4 py-3 text-left transition-colors last:border-0 hover:bg-[var(--pv-subtle)] ${n.read ? "opacity-60" : ""}`}>
                  <p className="flex items-center gap-2 text-xs font-bold text-[var(--pv-text)]">
                    {!n.read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" />}
                    {n.title}
                  </p>
                  {n.body && <p className="mt-0.5 break-words text-[11px] text-[var(--pv-text-4)]">{n.body}</p>}
                  <p className="mt-0.5 text-[10px] text-[var(--pv-text-4)]">{timeAgo(n.created_at)}</p>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
